import React from 'react';
import Alert from 'react-bootstrap/Alert';
import { DataSource } from '../../../enums/DataSource';
import { RecipeType } from '../../../enums/RecipeType';
import { RecipeOriginType } from '../../../enums/RecipeOriginType';
import { strings } from '../../../resources/strings';
import { recipeResources } from '../../../resources/recipeResources';
import { CustomerRecipe } from '../../../models/CustomerRecipe';
import { FormatHelper } from '../../../helpers/FormatHelper';
import { Button } from '../../../common-components/Button/Button';
import { CollapsibleCard } from '../../../common-components/CollapsibleCard/CollapsibleCard';
import { Table } from '../../../common-components/Table/Table';
import { TableRow } from '../../../common-components/TableRow/TableRow';
import { TableHead } from '../../../common-components/TableHead/TableHead';
import { TableData } from '../../../common-components/TableData/TableData';
import { AsideFlex } from '../../../common-components/AsideFlex/AsideFlex';
import { If } from '../../../common-components/If/If';
import {
  ConfirmationBlock, DateTimeBlock, DiagnosticBlock, DiagnosticContainerTable, DiagnosticDistanceType,
  FlexColumnBlock, HeaderCard, HeaderLeftContent, HeaderRightContent, RecipeFileBlock, RecipeIdentification, RecipeTypeDescription,
  RegistrationBlock, SecundaryRecipeInformation
} from './styles';

interface ICardCustomerRecipeProps {
  recipe: CustomerRecipe;
  linked?: boolean;
  readonly?: boolean;
  orderDataSource?: DataSource;
  showExpanded: boolean;
  editRecipe?: () => void;
  deleteRecipe?: () => void;
  linkRecipe?: () => void;
}

const FixedTableHeadDiagnostic = (
  {children}: {children?: React.ReactNode}
) => <TableHead normalText backgroundColor='#f5f5f5' borderStyle='1px solid #ddd' width='90px' textAlign='center'>{children}</TableHead>;

const TableHeadDiagnostic = (
  {children}: {children?: React.ReactNode}
) => <TableHead normalText backgroundColor='#f5f5f5' borderStyle='1px solid #ddd' textAlign='center'>{children}</TableHead>;

const FixedTableDataDiagnostic = (
  {children}: {children?: React.ReactNode}
) => <TableData borderStyle='1px solid #ddd' textAlign='center'>{children}</TableData>;

const TableDataDiagnostic = (
  {children}: {children?: React.ReactNode}
) => <TableData borderStyle='1px solid #ddd' width='90px' textAlign='center'>{children}</TableData>;

export const CardCustomerRecipe: React.FC<ICardCustomerRecipeProps> = ({
  recipe,
  linked,
  readonly,
  orderDataSource,
  showExpanded,
  editRecipe,
  deleteRecipe,
  linkRecipe
}) => {

  let identification = '';
  if (recipe.name && recipe.description) {
    identification = `${recipe.description} (${recipe.name}) - ${FormatHelper.formatDateTime(recipe.createdAt)}`;
  } else if (recipe.name || recipe.description) {
    identification = `${recipe.description || ''} ${recipe.name || ''} - ${FormatHelper.formatDateTime(recipe.createdAt)}`;
  } else {
    identification = FormatHelper.formatDateTime(recipe.createdAt);
  }

  const header = (
    <HeaderCard>
      <HeaderLeftContent>
        <RecipeTypeDescription>
          {strings.Recipe} {recipe.recipeType === RecipeType.Bifocal ? recipeResources.BifocalRecipeTypeDescription : recipeResources.SingleVisionRecipeTypeDescription}
          {linked ? ` ${strings.Linked}` : ''}
        </RecipeTypeDescription>
        <RecipeIdentification>
          {identification}
        </RecipeIdentification>
      </HeaderLeftContent>
      <HeaderRightContent>
        {linkRecipe ? <Button text={strings.UseThisRecipe} minWidth={'100px'} type='primary' click={linkRecipe} /> : null}
      </HeaderRightContent>
    </HeaderCard>
  );

  const buttons = (editRecipe || deleteRecipe) ? <>
    {editRecipe && <Button text={strings.Edit} minWidth={'100px'} type='secondary' click={editRecipe} />}
    {deleteRecipe && <Button text={strings.Delete} minWidth={'100px'} type='secondary' click={deleteRecipe} />}
  </> : null;

  const recipeFile = React.useMemo(() => {
    return (
      <RecipeFileBlock>
        {recipe.pathToFile
          ? <a href={`${recipe?.pathToFile}`} target='_blank'>Arquivo de receita anexo</a>
          : <span>Esta receita não possui arquivo anexo</span>}
      </RecipeFileBlock>
    )
  }, [recipe]);

  const diagnostics = React.useMemo(() => {

    if (!recipe.customerDiagnosticInformations || recipe.customerDiagnosticInformations.length === 0) {
      return <Alert variant='warning' style={{ marginTop: '10px' }}>A receita ainda não possui diagnóstico.</Alert>;
    }

    return (
      recipe.customerDiagnosticInformations.map(diagnostic => {
        const distanceTypeDescription = recipeResources.DistanceTypeOptions.find(rto => rto.Type === diagnostic.distanceType)?.Description;
        return (
          <DiagnosticBlock key={`diagnostic-${diagnostic.id}`}>
            <DiagnosticDistanceType>{distanceTypeDescription}</DiagnosticDistanceType>
            <DiagnosticContainerTable>
              <Table>
                <thead>
                  <TableRow>
                    <FixedTableHeadDiagnostic>{strings.Eye}</FixedTableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Spherical}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Cylindrical}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Axis}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.DNP}</TableHeadDiagnostic>
                    <TableHeadDiagnostic>{strings.Height}</TableHeadDiagnostic>
                  </TableRow>
                </thead>
                <tbody>
                  <TableRow>
                    <FixedTableDataDiagnostic>{strings.Right}</FixedTableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightSpherical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightCylindrical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightAxis}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.rightNasoPupillaryDistance}</TableDataDiagnostic>
                    <TableDataDiagnostic>{recipe.rightEyeHeight}</TableDataDiagnostic>
                  </TableRow>
                  <TableRow>
                    <FixedTableDataDiagnostic>{strings.Left}</FixedTableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftSpherical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftCylindrical}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftAxis}</TableDataDiagnostic>
                    <TableDataDiagnostic>{diagnostic.leftNasoPupillaryDistance}</TableDataDiagnostic>
                    <TableDataDiagnostic>{recipe.leftEyeHeight}</TableDataDiagnostic>
                  </TableRow>
                </tbody>
              </Table>
            </DiagnosticContainerTable>
          </DiagnosticBlock>
        )
      })
    );
  }, [recipe]);

  const confirmations = React.useMemo(() => {

    if (!recipe.customerDiagnosticInformations || recipe.customerDiagnosticInformations.length === 0) return null;

    return (
      <ConfirmationBlock>
        <AsideFlex side='left'>
          <FlexColumnBlock>
            <SecundaryRecipeInformation>
              {strings.ConfirmedRecipe}: {recipe.recipeApproved ? strings.Yes : strings.Not}
            </SecundaryRecipeInformation>
            <If condition={recipe.recipeApproved}>
              <DateTimeBlock>{FormatHelper.formatDateTime(recipe.recipeApprovedAt)}</DateTimeBlock>
            </If>
          </FlexColumnBlock>
        </AsideFlex>
        <AsideFlex side='right'>
          <FlexColumnBlock>
            <SecundaryRecipeInformation>
              {strings.ConfirmedDNP}: {recipe.nasoPupillaryDistanceApproved ? strings.Yes : strings.Not}
            </SecundaryRecipeInformation>
            <If condition={recipe.nasoPupillaryDistanceApproved}>
              <DateTimeBlock>{FormatHelper.formatDateTime(recipe.nasoPupillaryDistanceApprovedAt)}</DateTimeBlock>
            </If>
          </FlexColumnBlock>
        </AsideFlex>
      </ConfirmationBlock>
    );
  }, [recipe]);

  const registration = React.useMemo(() => {

    if (!recipe.customerDiagnosticInformations || recipe.customerDiagnosticInformations.length === 0) return null;

    return (
      <RegistrationBlock>
        <AsideFlex side='left'>
          <FlexColumnBlock>
            <SecundaryRecipeInformation>
              {recipe.createBy?.name 
                ? strings.RecipeRegisteredBy.replace('{user}', recipe.createBy?.name)
                : recipe.origin === RecipeOriginType.ShopifyPOS 
                  ? strings.RecipeRegisteredInPdv
                  : strings.RecipeRegisteredInCustomerArea}
            </SecundaryRecipeInformation>
            <DateTimeBlock>{FormatHelper.formatDateTime(recipe.createdAt)}</DateTimeBlock>
          </FlexColumnBlock>
        </AsideFlex>
        <AsideFlex side='right'>
          <If condition={!!recipe.updatedAt}>
            <FlexColumnBlock>
              <SecundaryRecipeInformation>
                {recipe.updatedBy?.name 
                  ? strings.RecipeUpdatedBy.replace('{user}', recipe.updatedBy?.name)
                  : recipe.origin === RecipeOriginType.ShopifyPOS 
                    ? strings.RecipeUpdatedInPdv
                    : strings.RecipeUpdatedInCustomerArea}
              </SecundaryRecipeInformation>
              <DateTimeBlock>{FormatHelper.formatDateTime(recipe.updatedAt)}</DateTimeBlock>
            </FlexColumnBlock>
          </If>
        </AsideFlex>
      </RegistrationBlock>
    );
  }, [recipe]);

  const multifocalInfo = React.useMemo(() => {
    if (!recipe.customerMultifocalInfo) return null;

    return (
      <DiagnosticBlock>
        <DiagnosticDistanceType>Multifocal</DiagnosticDistanceType>
        <DiagnosticContainerTable>
          <Table>
            <thead>
              <TableRow>
                <TableHeadDiagnostic>{strings.Addition}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.Bridge}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.Rim}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.Vertical}</TableHeadDiagnostic>
                <TableHeadDiagnostic>{strings.AbbreviationForLargestDiagonal}</TableHeadDiagnostic>
              </TableRow>
            </thead>
            <tbody>
              <TableRow>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.addField}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.actualBridge}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.actualEyeWidth}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.actualEyeHeight}</TableDataDiagnostic>
                <TableDataDiagnostic>{recipe.customerMultifocalInfo?.actualLargestDiagonal}</TableDataDiagnostic>
              </TableRow>
            </tbody>
          </Table>
        </DiagnosticContainerTable>
      </DiagnosticBlock>
    );
  }, [recipe]);

  return (
    <CollapsibleCard
      showExpanded={showExpanded}
      header={header}
      buttons={readonly ? null : buttons}
      marginBottom='5px'
    >
      {recipeFile}
      {diagnostics}
      {multifocalInfo}
      {confirmations}
      {registration}
    </CollapsibleCard>
  );
}